// §4.13's Pipeline stages card (P3-E2 PR E2-1, D-312 ruling 2): the tenant's
// stages in order with their probability and rotting days, on the sales
// settings screen.
//
// STAGES ARE PACK DATA. No control here adds, renames, reorders or removes a
// stage (no route does); a holder of `sales.tier.manage` tunes the two numbers
// and nothing else, and a later install leaves the tuning alone. The names
// come from the pack's four-locale bundle, never from the dictionaries.
//
// WHO SEES THE CARD. The settings screen is read by every `sales.read` holder,
// storekeeper and qc included, and the stages read is refused to both (§7
// narrowing 15). For a lineage the role mirror KNOWS is refused, the card is
// absent and no request is sent. A 403 for a reader the mirror cannot judge
// (a tenant's composed role) renders one sentence in the card's place,
// "Pipeline stages are not shown to your role", word for word the spec's.
//
// ONE ROW, ONE SAVE. A row's Save sends only the numbers that changed, and a
// cleared rotting box sends `rotting_after_days: null`, which means a deal
// never rots in that stage. A stage that no longer exists (404) says so and
// the list reads again.
//
// OFFLINE. A stages read held because the browser has no network says so in
// the card (design-system §3.9), and the skeleton is only ever for a read in
// flight.

import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "../../../../../components/button";
import { Card } from "../../../../../components/card";
import { HelpTip } from "../../../../../components/help-tip";
import { DataTable, type DataTableColumn } from "../../../../../components/data-table";
import {
  CardSkeleton,
  EmptyState,
  ErrorState,
  OfflineState,
} from "../../../../../components/states";
import { RequestError, requestIdOf } from "../../../../../lib/request";
import { MutationFailureNote } from "../../mutation-failure-note";
import { stageName } from "../../lib/pipeline";
import { isSalesReadRefused, useSalesPermissions } from "../../hooks/permissions";
import {
  usePipelineStages,
  useUpdatePipelineStage,
  type PipelineStage,
  type PipelineStageUpdateRequest,
} from "../../hooks/opportunity-queries";
import { guideHref } from "../../../../../lib/guide-links";

/** The typed values of one row's two boxes. */
export interface StageDraft {
  readonly probability: string;
  readonly rotting: string;
}

export function draftOf(
  stage: Pick<PipelineStage, "probability_pct" | "rotting_after_days">,
): StageDraft {
  return {
    probability: String(stage.probability_pct),
    rotting: stage.rotting_after_days === null ? "" : String(stage.rotting_after_days),
  };
}

const WHOLE = /^\d{1,3}$/u;

/** The row's client checks, the contract's own bounds (pure, unit-tested). */
export function stageDraftErrors(draft: StageDraft): { probability?: true; rotting?: true } {
  const errors: { probability?: true; rotting?: true } = {};
  const probability = draft.probability.trim();
  if (!WHOLE.test(probability) || Number(probability) > 100) errors.probability = true;
  const rotting = draft.rotting.trim();
  if (
    rotting.length > 0 &&
    (!WHOLE.test(rotting) || Number(rotting) < 1 || Number(rotting) > 365)
  ) {
    errors.rotting = true;
  }
  return errors;
}

/** The PATCH body: only what changed, and a cleared rotting box as null. */
export function stagePatchBody(
  stage: Pick<PipelineStage, "probability_pct" | "rotting_after_days">,
  draft: StageDraft,
): PipelineStageUpdateRequest {
  const body: { probability_pct?: number; rotting_after_days?: number | null } = {};
  const probability = Number(draft.probability.trim());
  if (probability !== stage.probability_pct) body.probability_pct = probability;
  const rottingText = draft.rotting.trim();
  const rotting = rottingText.length === 0 ? null : Number(rottingText);
  if (rotting !== stage.rotting_after_days) body.rotting_after_days = rotting;
  return body;
}

export function PipelineStagesCard() {
  const perms = useSalesPermissions();
  // The mirror KNOWS this lineage is refused: no card and no request.
  if (isSalesReadRefused(perms, perms.canOpenPipeline)) return null;
  return <PipelineStagesBody />;
}

function PipelineStagesBody() {
  const { t, i18n } = useTranslation();
  const perms = useSalesPermissions();
  const canEdit = perms.has("sales.tier.manage");
  const stages = usePipelineStages(true);
  const update = useUpdatePipelineStage();
  const [drafts, setDrafts] = useState<Readonly<Record<string, StageDraft>>>({});
  const [savedId, setSavedId] = useState<string | null>(null);
  const [savingId, setSavingId] = useState<string | null>(null);
  const [failedId, setFailedId] = useState<string | null>(null);

  if (stages.error instanceof RequestError && stages.error.httpStatus === 403) {
    return (
      <p className="sales-hint" data-testid="sales-settings-stages-refused">
        {t("sales.settings.stagesRefused")}
      </p>
    );
  }

  const draftFor = (stage: PipelineStage): StageDraft => drafts[stage.id] ?? draftOf(stage);
  const setDraft = (id: string, next: StageDraft) => {
    setSavedId(null);
    setFailedId(null);
    setDrafts((all) => ({ ...all, [id]: next }));
  };

  const columns: DataTableColumn<PipelineStage>[] = [
    {
      key: "name",
      header: t("sales.settings.stagesColStage"),
      id: true,
      render: (stage) => stageName(stage, i18n.language),
    },
    {
      key: "probability_pct",
      header: t("sales.settings.stagesColProbability"),
      numeric: true,
      render: (stage) => {
        if (!canEdit) return <span className="num">{stage.probability_pct}</span>;
        const draft = draftFor(stage);
        const bad = stageDraftErrors(draft).probability === true;
        return (
          <span className="sales-stage-cell">
            <input
              className="sales-stage-input"
              inputMode="numeric"
              value={draft.probability}
              aria-label={t("sales.settings.stagesColProbability")}
              aria-invalid={bad || undefined}
              data-testid="sales-settings-stage-probability"
              onChange={(event) => {
                setDraft(stage.id, { ...draft, probability: event.target.value });
              }}
            />
            {bad ? (
              <span
                className="field-error"
                role="alert"
                data-testid="sales-settings-stage-error-probability"
              >
                {t("sales.settings.stagesProbabilityRange")}
              </span>
            ) : null}
          </span>
        );
      },
    },
    {
      key: "rotting_after_days",
      header: t("sales.settings.stagesColRotting"),
      numeric: true,
      render: (stage) => {
        if (!canEdit) {
          return stage.rotting_after_days === null ? (
            t("sales.settings.stagesRottingNever")
          ) : (
            <span className="num">{stage.rotting_after_days}</span>
          );
        }
        const draft = draftFor(stage);
        const bad = stageDraftErrors(draft).rotting === true;
        return (
          <span className="sales-stage-cell">
            <input
              className="sales-stage-input"
              inputMode="numeric"
              value={draft.rotting}
              placeholder={t("sales.settings.stagesRottingNever")}
              aria-label={t("sales.settings.stagesColRotting")}
              aria-invalid={bad || undefined}
              data-testid="sales-settings-stage-rotting"
              onChange={(event) => {
                setDraft(stage.id, { ...draft, rotting: event.target.value });
              }}
            />
            {bad ? (
              <span
                className="field-error"
                role="alert"
                data-testid="sales-settings-stage-error-rotting"
              >
                {t("sales.settings.stagesRottingRange")}
              </span>
            ) : null}
          </span>
        );
      },
    },
    ...(canEdit
      ? [
          {
            key: "actions",
            header: t("sales.settings.stagesSave"),
            actions: true,
            render: (stage: PipelineStage) => {
              const draft = draftFor(stage);
              const invalid = Object.keys(stageDraftErrors(draft)).length > 0;
              const body = invalid ? {} : stagePatchBody(stage, draft);
              const changed = Object.keys(body).length > 0;
              return (
                <Button
                  size="sm"
                  data-testid="sales-settings-stage-save"
                  disabled={invalid || !changed || update.isPending}
                  busy={update.isPending && savingId === stage.id}
                  busyLabel={t("sales.settings.stagesSaving")}
                  onClick={() => {
                    setSavingId(stage.id);
                    setSavedId(null);
                    setFailedId(null);
                    update.mutate(
                      { id: stage.id, body },
                      {
                        onSuccess: () => {
                          setSavedId(stage.id);
                          // The saved row reads the server's numbers again.
                          setDrafts((all) =>
                            Object.fromEntries(
                              Object.entries(all).filter(([id]) => id !== stage.id),
                            ),
                          );
                        },
                        onError: () => {
                          setFailedId(stage.id);
                        },
                      },
                    );
                  }}
                >
                  {t("sales.settings.stagesSave")}
                </Button>
              );
            },
          } satisfies DataTableColumn<PipelineStage>,
        ]
      : []),
  ];

  const gone =
    update.error instanceof RequestError && update.error.code === "PIPELINE_STAGE_NOT_FOUND";

  return (
    <Card
      className="sales-360-card sales-settings-stages"
      data-testid="sales-settings-stages"
      title={t("sales.settings.stagesTitle")}
      action={
        <HelpTip
          label={t("nav.helpAbout", { topic: t("sales.settings.stagesTitle") })}
          data-testid="sales-settings-stages-help"
          guide={{
            href: guideHref(i18n.language, "sales/deals-and-tasks"),
            label: t("nav.helpRead"),
          }}
        >
          {t("sales.settings.stagesBody")}
        </HelpTip>
      }
    >
      {stages.data === undefined ? (
        stages.error !== null ? (
          <ErrorState
            title={t("sales.settings.stagesError")}
            retryLabel={t("sales.common.retry")}
            requestIdLabel={t("sales.common.requestId")}
            requestId={requestIdOf(stages.error)}
            onRetry={() => {
              void stages.refetch();
            }}
          />
        ) : stages.fetchStatus === "paused" ? (
          // Held until the network returns (design-system §3.9).
          <div data-testid="sales-settings-stages-offline">
            <OfflineState title={t("sales.offline.title")} body={t("sales.offline.body")} />
          </div>
        ) : (
          <CardSkeleton lines={5} label={t("sales.settings.stagesLoading")} />
        )
      ) : stages.data.items.length === 0 ? (
        <div data-testid="sales-settings-stages-empty">
          <EmptyState
            title={t("sales.pipeline.noStagesTitle")}
            body={
              perms.isAdmin ? t("sales.pipeline.noStagesAdmin") : t("sales.pipeline.noStagesBody")
            }
          />
        </div>
      ) : (
        <>
          <DataTable
            data-testid="sales-settings-stage-table"
            columns={columns}
            rows={stages.data.items}
            rowKey={(stage) => stage.id}
            caption={t("sales.settings.stagesCaption")}
            rowTestId="sales-settings-stage-row"
            rowProps={(stage) => ({ "data-stage-id": stage.id, "data-stage-code": stage.code })}
            empty={null}
          />
          {canEdit ? <p className="sales-hint">{t("sales.settings.stagesRottingHint")}</p> : null}
        </>
      )}
      {savedId !== null ? (
        <p className="sales-hint" role="status" data-testid="sales-settings-stage-saved">
          {t("sales.settings.stagesSaved")}
        </p>
      ) : null}
      {failedId !== null && gone ? (
        <p className="sales-error-note" role="alert" data-testid="sales-settings-stages-gone">
          {t("sales.settings.stagesGone")}
        </p>
      ) : null}
      {failedId !== null && !gone ? (
        <MutationFailureNote
          data-testid="sales-settings-stage-save-failed"
          text={t("sales.settings.stagesSaveFailed")}
          error={update.error}
        />
      ) : null}
    </Card>
  );
}
